import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';


const API_BASE = '/api';
const ENV_MARKER = '\n\n---\n[자동 환경정보]\n';

const readError = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

const splitNote = (text) => {
  const value = String(text || '');
  const index = value.indexOf(ENV_MARKER);
  if (index < 0) return { content: value, environment: '' };
  return {
    content: value.slice(0, index).trimEnd(),
    environment: value.slice(index + ENV_MARKER.length).trim(),
  };
};

const responseData = (res) => res?.data?.data || {};

export default function FarmDiaryListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [farms, setFarms] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const farmId = searchParams.get('farm_id') || '';
  const keyword = searchParams.get('keyword') || '';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  useEffect(() => {
    axios.get(`${API_BASE}/farms`)
      .then((res) => {
        const list = res.data?.data || [];
        setFarms(list);
        if (!farmId && list.length) {
          const next = new URLSearchParams(searchParams);
          next.set('farm_id', String(list[0].id));
          setSearchParams(next, { replace: true });
        }
      })
      .catch((e) => setError(readError(e, '농장 목록을 불러오지 못했습니다.')));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!farmId) {
      setNotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/farms/${farmId}/diary-notes`);
      setNotes(responseData(res).notes || []);
    } catch (e) {
      setError(readError(e, '영농일지를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return notes.filter((note) => {
      const day = String(note.date_time || '').slice(0, 10);
      const text = `${note.name || ''} ${splitNote(note.note).content}`.toLowerCase();
      if (q && !text.includes(q)) return false;
      if (dateFrom && day < dateFrom) return false;
      if (dateTo && day > dateTo) return false;
      return true;
    });
  }, [notes, keyword, dateFrom, dateTo]);

  const farmName = farms.find((f) => String(f.id) === String(farmId))?.name || '-';

  return (
    <div className="container-fluid py-4 farm-diary-page" style={{ maxWidth: 1380 }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">영농일지</h2>
          <div className="text-secondary">농장 작업 기록을 날짜별로 조회합니다.</div>
        </div>
        <div className="d-flex gap-2">
          <Link className="btn btn-outline-secondary" to="/farm-diary/settings">농장 설정</Link>
          <Link className="btn btn-success" to={`/farm-diary/write?farm_id=${farmId}`}>+ 일지 작성</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body p-3">
          <div className="d-flex gap-2 align-items-center">
            <select
              className="form-select flex-grow-1"
              value={farmId}
              onChange={(e) => setFilter('farm_id', e.target.value)}
            >
              {farms.map((farm) => (
                <option key={farm.id} value={farm.id}>{farm.name}</option>
              ))}
            </select>
            <button
              type="button"
              className={`btn flex-shrink-0 ${showFilters || keyword || dateFrom || dateTo ? 'btn-secondary' : 'btn-outline-secondary'}`}
              onClick={() => setShowFilters((v) => !v)}
            >
              검색/필터
            </button>
          </div>

          {showFilters && (
            <div className="row g-2 mt-1">
              <div className="col-6 col-md-2">
                <label className="form-label small mb-1">시작일</label>
                <input type="date" className="form-control" value={dateFrom} onChange={(e) => setFilter('date_from', e.target.value)} />
              </div>
              <div className="col-6 col-md-2">
                <label className="form-label small mb-1">종료일</label>
                <input type="date" className="form-control" value={dateTo} onChange={(e) => setFilter('date_to', e.target.value)} />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small mb-1">검색</label>
                <input className="form-control" placeholder="제목 또는 내용 검색" value={keyword} onChange={(e) => setFilter('keyword', e.target.value)} />
              </div>
              <div className="col-12 col-md-2 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={() => setSearchParams(farmId ? { farm_id: farmId } : {})}
                >
                  초기화
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>총 {filtered.length}건</strong>
        <button
          className="btn btn-link btn-sm p-2 text-secondary text-decoration-none"
          onClick={load}
        >
          새로고침
        </button>
      </div>

      {loading ? (
        <div className="card border-0 shadow-sm">
          <div className="py-5 text-center">
            <div className="spinner-border text-success" />
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card border-0 shadow-sm">
          <div className="py-5 text-center text-secondary">
            등록된 영농일지가 없습니다.
          </div>
        </div>
      ) : (
        <>
          {/* 모바일 목록 */}
          <div className="d-md-none">
            <div className="d-flex flex-column gap-3">
              {filtered.map((note) => {
                const body = splitNote(note.note).content;
                const day =
                  String(note.date_time || '').slice(0, 10) || '-';
                const fileCount = (note.files || []).length;

                return (
                  <Link
                    key={note.unique_id}
                    to={`/farm-diary/detail/${note.unique_id}?farm_id=${farmId}`}
                    className="card border-0 shadow-sm text-decoration-none text-dark"
                  >
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                        <span className="small text-secondary">
                          {day}
                        </span>

                        {fileCount > 0 && (
                          <span className="badge text-bg-light">
                            첨부 {fileCount}개
                          </span>
                        )}
                      </div>

                      <div className="fw-bold fs-5 mb-2">
                        {note.name || '제목 없음'}
                      </div>

                      <div
                        className="text-secondary mb-3"
                        style={{
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          wordBreak: 'break-word',
                        }}
                      >
                        {body || '-'}
                      </div>

                      <div className="d-flex justify-content-between align-items-center border-top pt-3">
                        <span className="small text-secondary text-truncate me-3">
                          {farmName}
                        </span>

                        <span className="small fw-semibold text-success flex-shrink-0">
                          보기
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* 태블릿 / PC 목록 */}
          <div className="card border-0 shadow-sm overflow-hidden d-none d-md-block">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>작업일</th>
                    <th>제목</th>
                    <th>농장</th>
                    <th>내용</th>
                    <th>첨부</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {filtered.map((note) => {
                    const body = splitNote(note.note).content;

                    return (
                      <tr key={note.unique_id}>
                        <td className="text-nowrap">
                          {String(note.date_time || '').slice(0, 10) || '-'}
                        </td>

                        <td>
                          <Link
                            className="fw-semibold text-decoration-none text-dark"
                            to={`/farm-diary/detail/${note.unique_id}?farm_id=${farmId}`}
                          >
                            {note.name}
                          </Link>
                        </td>

                        <td>{farmName}</td>

                        <td>
                          <div
                            className="text-truncate"
                            style={{ maxWidth: 360 }}
                          >
                            {body || '-'}
                          </div>
                        </td>

                        <td>{(note.files || []).length}개</td>

                        <td className="text-end">
                          <Link
                            className="btn btn-sm btn-outline-success"
                            to={`/farm-diary/detail/${note.unique_id}?farm_id=${farmId}`}
                          >
                            보기
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}